import { t } from "@/lib/i18n/interpolate";
import type {
  DocBlock,
  DocField,
  DocGridCell,
  DocSection,
  DocumentModel,
} from "@/lib/tool-kit/document";
import { splitItems } from "@/lib/tool-kit/essay-templates/text";

/**
 * A PESTLE analysis as a six-box grid — political, economic, social, technological,
 * legal, environmental — with a rating table that lists every factor the writer entered
 * and leaves its impact and timing to be judged by hand, then "so what?" prompts that
 * turn the grid into a decision. `pest` drops the last two boxes for courses that teach
 * the four-factor version.
 */

export const PESTLE_FACTORS = [
  "political",
  "economic",
  "social",
  "technological",
  "legal",
  "environmental",
] as const;

export type PestleFactor = (typeof PESTLE_FACTORS)[number];

export type PestleVariant = "pest" | "pestle";

/** The factors each variant uses, in the order of the acronym. */
export const VARIANT_FACTORS: Record<PestleVariant, readonly PestleFactor[]> = {
  pest: ["political", "economic", "social", "technological"],
  pestle: PESTLE_FACTORS,
};

export type PestleInput = Record<PestleFactor, string> & {
  subject: string;
  objective: string;
  scope: string;
  horizon: string;
  variant: PestleVariant;
};

export interface PestleLabels {
  defaultTitle: string;
  titleWithSubject: string;
  subtitle: Record<PestleVariant, string>;
  fields: { objective: string; scope: string; horizon: string };
  contextPrompt: string;
  factorLabels: Record<PestleFactor, string>;
  captions: Record<PestleFactor, string>;
  factorPrompts: Record<PestleFactor, string>;
  gridNote: string;
  ratingHeading: string;
  ratingNote: string;
  ratingHead: { factor: string; item: string; impact: string; timing: string };
  ratingPrompt: string;
  soWhatHeading: string;
  soWhatNote: string;
  soWhatSteps: readonly string[];
  sourcesHeading: string;
  sourcesNote: string;
  sourcesPrompt: string;
}

/** The writer's items for one factor, one per line. */
export function factorItems(
  input: PestleInput,
  factor: PestleFactor
): string[] {
  return splitItems(input[factor]);
}

/** Every entered item with its factor, in acronym order — the rows of the rating table. */
export function ratingRows(
  input: PestleInput,
  labels: PestleLabels
): string[][] {
  return VARIANT_FACTORS[input.variant].flatMap((factor) =>
    factorItems(input, factor).map((item) => [
      labels.factorLabels[factor],
      item,
      "",
      "",
    ])
  );
}

function contextBlock(input: PestleInput, labels: PestleLabels): DocBlock {
  const entries: [string, string][] = [
    [labels.fields.objective, input.objective],
    [labels.fields.scope, input.scope],
    [labels.fields.horizon, input.horizon],
  ];
  const fields: DocField[] = entries
    .filter(([, value]) => value.trim() !== "")
    .map(([label, value]) => ({ label, value: value.trim() }));
  if (fields.length === 0) {
    return { kind: "prompt", text: labels.contextPrompt };
  }
  return { kind: "fields", fields };
}

function factorCells(input: PestleInput, labels: PestleLabels): DocGridCell[] {
  return VARIANT_FACTORS[input.variant].map((factor) => ({
    title: labels.factorLabels[factor],
    caption: labels.captions[factor],
    items: factorItems(input, factor),
    prompt: labels.factorPrompts[factor],
  }));
}

function ratingBlocks(input: PestleInput, labels: PestleLabels): DocBlock[] {
  const rows = ratingRows(input, labels);
  const body: DocBlock =
    rows.length > 0
      ? {
          kind: "table",
          head: [
            labels.ratingHead.factor,
            labels.ratingHead.item,
            labels.ratingHead.impact,
            labels.ratingHead.timing,
          ],
          rows,
        }
      : { kind: "prompt", text: labels.ratingPrompt };
  return [{ kind: "guidance", text: labels.ratingNote }, body];
}

export function buildPestle(
  input: PestleInput,
  labels: PestleLabels
): DocumentModel {
  const subject = input.subject.trim();
  const sections: DocSection[] = [
    {
      blocks: [
        contextBlock(input, labels),
        { kind: "grid", cells: factorCells(input, labels) },
        { kind: "guidance", text: labels.gridNote },
      ],
    },
    { heading: labels.ratingHeading, blocks: ratingBlocks(input, labels) },
    {
      heading: labels.soWhatHeading,
      blocks: [
        { kind: "guidance", text: labels.soWhatNote },
        {
          kind: "list",
          list: {
            style: "decimal",
            items: labels.soWhatSteps.map((text) => ({ text, prompt: true })),
          },
        },
      ],
    },
    {
      heading: labels.sourcesHeading,
      blocks: [
        { kind: "guidance", text: labels.sourcesNote },
        { kind: "prompt", text: labels.sourcesPrompt },
      ],
    },
  ];
  return {
    title: subject
      ? t(labels.titleWithSubject, { subject })
      : labels.defaultTitle,
    subtitle: labels.subtitle[input.variant],
    sections,
  };
}
